import { ORDER_STATUS_LABELS, SOURCE_ICONS, SOURCE_LABELS, percent, titleCase } from '@/lib/format'

export function StatusBadge({ status }) {
  return (
    <span className={`badge status-${status}`}>
      {ORDER_STATUS_LABELS[status] ?? titleCase(status)}
    </span>
  )
}

export function SourceBadge({ source }) {
  return (
    <span className="source-badge" title={SOURCE_LABELS[source] ?? source}>
      {SOURCE_ICONS[source] ?? '•'} {SOURCE_LABELS[source] ?? source}
    </span>
  )
}

/**
 * Confidence is the number a reviewer acts on, so it's colour-coded rather
 * than left as a bare decimal: green is safe to approve at a glance, red
 * means read the original message before touching anything.
 */
export function ConfidenceBadge({ value }) {
  if (value == null) return <span className="badge conf-none">—</span>
  const n = Number(value)
  const level = n >= 0.88 ? 'high' : n >= 0.55 ? 'medium' : 'low'
  return <span className={`badge conf-${level}`}>{percent(n)}</span>
}

export function MatchBadge({ status }) {
  // 'Set by staff' and 'Customer chose' are both a person picking, and the
  // difference is which person: staff correcting a line means the matcher
  // failed, a customer answering means it asked and got an answer.
  const labels = {
    auto: 'Auto-matched',
    suggested: 'Needs check',
    manual: 'Set by staff',
    confirmed: 'Customer chose',
    unmatched: 'No match',
  }
  return <span className={`badge match-${status}`}>{labels[status] ?? status}</span>
}

export function Empty({ icon = '📭', title, hint }) {
  return (
    <div className="empty">
      <div className="empty-icon">{icon}</div>
      <div className="empty-title">{title}</div>
      {hint && <div className="empty-hint">{hint}</div>}
    </div>
  )
}

export function Loading({ label = 'Loading…' }) {
  return <div className="loading">{label}</div>
}

/**
 * A grey block standing in for content that has not arrived.
 *
 * Preferred over 'Loading…' wherever the shape of what is coming is already
 * known: the page keeps its layout, so nothing jumps when the data lands,
 * and the wait reads as 'nearly there' rather than 'nothing here'.
 *
 * `w` and `h` take any CSS length. Decorative, so it is hidden from screen
 * readers — the region it sits in carries aria-busy instead.
 */
export function Skeleton({ w = '100%', h = 14, radius = 6, className = '' }) {
  return (
    <span
      aria-hidden="true"
      className={`skeleton ${className}`}
      style={{ width: w, height: typeof h === 'number' ? `${h}px` : h, borderRadius: radius }}
    />
  )
}

/** Rows of skeleton cells, for a table whose columns are already known. */
export function SkeletonRows({ rows = 5, cols = 4 }) {
  return Array.from({ length: rows }, (_, r) => (
    <tr key={r}>
      {Array.from({ length: cols }, (_, c) => (
        <td key={c}>
          <Skeleton w={c === 0 ? '70%' : '45%'} />
        </td>
      ))}
    </tr>
  ))
}

/**
 * A dismissible banner for the result of an action.
 *
 * `role="alert"` so a screen reader announces it without the user having to
 * go looking — the whole point of this element is that something happened
 * away from where they are looking.
 */
export function Alert({ tone = 'success', message, onClose, reserve = false }) {
  // `reserve` keeps the box in the layout while it is empty, so a message
  // appearing or clearing does not push the page around. Hidden rather than
  // sized by hand: the space then matches a real alert exactly, whatever the
  // padding or font becomes later.
  if (!message) {
    if (!reserve) return null
    return (
      // The same markup as a real alert, close button included: without it
      // the placeholder is a few pixels shorter and the table still shifts.
      <div className={`alert alert-${tone}`} style={{ visibility: 'hidden' }} aria-hidden="true">
        <span>{'\u00a0'}</span>
        <button type="button" className="alert-close" tabIndex={-1}>
          ×
        </button>
      </div>
    )
  }
  return (
    <div className={`alert alert-${tone}`} role="alert">
      <span>{message}</span>
      {onClose && (
        <button type="button" className="alert-close" onClick={onClose} aria-label="Dismiss">
          ×
        </button>
      )}
    </div>
  )
}

export function ErrorNote({ message, onRetry }) {
  return (
    <div className="error-note">
      <span>⚠️ {message}</span>
      {onRetry && (
        <button type="button" className="btn btn-sm" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  )
}

export function StatCard({ label, value, hint, tone = 'default' }) {
  return (
    <div className={`stat-card tone-${tone}`}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {hint && <div className="stat-hint">{hint}</div>}
    </div>
  )
}

export function Toolbar({ children }) {
  return <div className="toolbar">{children}</div>
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        {subtitle && <p className="page-subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  )
}

export function Pagination({ page, pageSize, total, onChange }) {
  const pages = Math.max(1, Math.ceil(total / pageSize))
  if (pages <= 1) return null
  const first = (page - 1) * pageSize + 1
  const last = Math.min(page * pageSize, total)
  return (
    <div className="pagination">
      <button
        type="button"
        className="btn btn-sm"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
      >
        Previous
      </button>
      <span className="page-info">
        {/* The range rather than a bare page number: it says how many rows
            are on this page without anyone counting them, and on the last
            page it says how few — '51–54 of 54' is the answer to 'is that
            everything?'. The per-page figure is stated too, because 'of 2
            pages' means nothing until you know what a page holds. */}
        {first}–{last} of {total} · {pageSize} per page · page {page} of {pages}
      </span>
      <button
        type="button"
        className="btn btn-sm"
        disabled={page >= pages}
        onClick={() => onChange(page + 1)}
      >
        Next
      </button>
    </div>
  )
}
